/**
 * Matches — the club's season, and the way into a match.
 *
 * DELIBERATELY FOUR COLUMNS. The API returns xG, xPTS, cumulative GD, league
 * position and a dozen other figures; none of them are shown, because nobody
 * has said where they come from or what they mean here (Ben, 2026-08-28).
 * Showing a number you cannot explain is worse than not showing it.
 *
 * A row is a link only when a game FILE exists behind it. Fixtures without
 * one have nothing to open, and a row that looks clickable and is not teaches
 * people to distrust the ones that are.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError, api, apiEnvelope, type Session } from '../lib/api'
import { pathSeg } from '../lib/urls'
import WorthALook from '../components/WorthALook'
import MatchDate, { matchDay } from './MatchDate'
import MatchScore, { type LabeledScore, type Score } from './MatchScore'
import RenameMatch from './RenameMatch'
import { ppdaForTeam, type TeamPpda } from './match/TeamSplitPanels'
import './games.css'

type Fixture = {
  game_code: string | null
  gamefile_id: number | null
  home_team: string | null
  away_team: string | null
  home_team_logo: string | null
  away_team_logo: string | null
  date: string | null
  game_id: number | null
  home_score: number | null
  away_score: number | null
  /** Staff only: what the GOAL labels add up to (services/match_score). */
  goal_score?: LabeledScore | null
  /** Staff only: served from priori-stack-v1, so it has a PPDA to fetch. */
  stack_served?: boolean
  /** The match date alone (YYYY-MM-DD) or null; `date` falls back to the upload. */
  match_date?: string | null
  /** 'home' | 'away' when the club plays; null for two other teams */
  our_side: 'home' | 'away' | null
}
type State =
  | { status: 'loading' }
  | { status: 'ready'; fixtures: Fixture[] }
  | { status: 'error'; message: string }


/** The list's day label: MatchDate.matchDay (a calendar day, no timezone shift). */
const matchDate = matchDay

/** The year a list date falls in ("2026-08-27 ..." -> "2026"), or null. */
const yearOf = (date: string | null) =>
  date && /^\d{4}-/.test(date) ? date.slice(0, 4) : null

/** Rows grouped by year, the newest year first whatever the date sort (it
 *  orders the rows WITHIN a year, which keep their order); undated matches
 *  last, as "No date". */
export function byYear<T extends { date: string | null }>(rows: T[]) {
  const groups = new Map<string, T[]>()
  for (const r of rows) {
    const y = yearOf(r.date) ?? 'No date'
    groups.set(y, [...(groups.get(y) ?? []), r])
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === 'No date' ? 1 : b === 'No date' ? -1 : b.localeCompare(a)))
    .map(([year, rs]) => ({ year, rows: rs }))
}

function Team({ name, logo }: { name: string | null; logo: string | null }) {
  // A URL that 404s degrades to the quiet placeholder, not the browser's
  // broken-image glyph — same posture as ClubLogo. It happens for real: a
  // local backend with no bucket credentials serves only the bundled set.
  const [broken, setBroken] = useState(false)
  return (
    <span className="gm-team">
      {logo && !broken
        ? <img className="gm-team__logo" src={logo} alt="" loading="lazy"
               onError={() => setBroken(true)} />
        : <span className="gm-team__logo gm-team__logo--none" aria-hidden />}
      <span className="gm-team__name">{name ?? '—'}</span>
    </span>
  )
}

/** At most this many PPDA requests in flight from the list at once: each
 *  one reads serve tables from the bucket, and a season is ~80 rows. */
export const PPDA_MAX_INFLIGHT = 4
let ppdaInflight = 0
const ppdaWaiting: Array<() => void> = []
/** Run `job` when a slot is free; the returned function withdraws it if it
 *  has not started (a row scrolled away or the list unmounted). */
function ppdaSlot(job: () => Promise<void>): () => void {
  let withdrawn = false
  const start = () => {
    if (withdrawn) { next(); return }
    ppdaInflight += 1
    job().finally(() => { ppdaInflight -= 1; next() })
  }
  const next = () => {
    const run = ppdaWaiting.shift()
    if (run) run()
  }
  if (ppdaInflight < PPDA_MAX_INFLIGHT) start()
  else ppdaWaiting.push(start)
  return () => { withdrawn = true }
}

const ppdaText = (v: number | null | undefined) =>
  typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : '\u2014'

/** Staff only: each team's PPDA under the score, home first like the score
 *  ("PPDA 3.58 · 6.10"). Fetched from the match page's own endpoint once
 *  the row is on screen, and only for a stack-served game: a legacy game's
 *  PPDA is another measure (get_team_ppda) and shows nothing. A failed
 *  fetch shows nothing too; a team with no value shows a dash, never 0. */
export function ListPpda({ gameCode }: { gameCode: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [visible, setVisible] = useState(false)
  const [data, setData] = useState<TeamPpda | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect() }
    }, { rootMargin: '200px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!visible) return
    let cancelled = false
    const withdraw = ppdaSlot(async () => {
      if (cancelled) return
      try {
        const body = await apiEnvelope<TeamPpda & { success?: boolean }>(
          `/api/v1/game-match-summary/ppda/${pathSeg(gameCode)}`)
        if (!cancelled && body?.success && body.ppda) setData(body)
      } catch {
        // No PPDA for this row; the list is unaffected.
      }
    })
    return () => { cancelled = true; withdraw() }
  }, [visible, gameCode])

  // The response is already keyed home/away: index 0 stands for home here,
  // so ppdaForTeam applies its one source rule (legacy draws nothing).
  const home = ppdaForTeam(data, 0, 0)
  const away = ppdaForTeam(data, 1, 0)
  if (home === undefined || away === undefined) {
    return <span ref={ref} className="gm-ppda" aria-hidden />
  }
  const title = 'Passes per defensive action, home · away'
    + (data?.definition ? `: ${data.definition}` : '.') + ' Lower is a more intense press.'
  return (
    <span ref={ref} className="gm-ppda" title={title} data-testid="list-ppda">
      PPDA {ppdaText(home)} · {ppdaText(away)}
    </span>
  )
}

/** Staff only: which side of the match is the club's, or "not ours"
 *  (an opponent match). Writes game_facts.org_side — the same record the
 *  Layers page's Club side sets. Clicks stay in the cell, not the row link. */
function ClubSide({ gameId, side, onSaved }: {
  gameId: number | null
  side: 'home' | 'away' | null
  onSaved: (s: 'home' | 'away' | null) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (gameId == null) return null
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()
  return (
    <span onClick={stop} onKeyDown={stop}>
      <select className="adm-select gm-side" aria-label="Club side"
              value={side ?? ''} disabled={busy}
              onChange={async (e) => {
                const next = (e.target.value || null) as 'home' | 'away' | null
                setBusy(true); setError(null)
                try {
                  await api(`/api/v1/fixtures/games/${gameId}/club-side`,
                            { method: 'POST', body: JSON.stringify({ club_side: next }) })
                  onSaved(next)
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : String(err))
                } finally { setBusy(false) }
              }}>
        <option value="home">Ours · home</option>
        <option value="away">Ours · away</option>
        <option value="">Opponent match</option>
      </select>
      {error && <span className="gm-score-error" role="alert">{error}</span>}
    </span>
  )
}

/** The one season list, serving two rooms: the Match Room opens the match
 *  page, the Clip Room opens the same match in the Clip Lab. One component,
 *  so the rooms cannot drift apart in columns, states or affordances. */
export default function Games({ session, room = 'match', embedded = false }:
    { session: Session; room?: 'match' | 'clip'; embedded?: boolean }) {
  // Router navigation, not window.location: a row click is an in-app hop,
  // and a full document load re-fetched the session and every asset
  // (reported as "the page reloads", Ben 2026-10-23).
  const navigate = useNavigate()
  const title = room === 'clip' ? 'Clip Room' : 'Match Room'
  const rowTarget = room === 'clip' ? '/clip-room' : '/match-room'
  const rowOpens = room === 'clip'
    ? 'Rows with film open the match in the Clip Lab.'
    : 'Rows with film open the match summary.'
  const [state, setState] = useState<State>({ status: 'loading' })
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  // A rename changes the code, the teams and the link: read the list again
  const [reload, setReload] = useState(0)
  // Priori staff enter scores by hand for now (MatchScore).
  const canEditScore = !!(session.user.is_staff || session.user.is_platform_admin)
  // Staff only, and the Match Room only (Ben, 2026-10-25): each team's PPDA
  // under the score. The server sends stack_served to staff alone, so a
  // club's list never has a row to fetch for either way.
  const showPpda = canEditScore && room === 'match'
  const setScore = (gamefileId: number | null, sc: Score) =>
    setState((st) => st.status !== 'ready' ? st : { ...st,
      fixtures: st.fixtures.map((f) => (f.gamefile_id === gamefileId
        ? { ...f, home_score: sc.home, away_score: sc.away } : f)) })
  const setDate = (gamefileId: number | null, d: string | null) =>
    setState((st) => st.status !== 'ready' ? st : { ...st,
      fixtures: st.fixtures.map((f) => (f.gamefile_id === gamefileId
        ? { ...f, match_date: d, date: d ?? f.date } : f)) })
  const setSide = (gamefileId: number | null, side: 'home' | 'away' | null) =>
    setState((st) => st.status !== 'ready' ? st : { ...st,
      fixtures: st.fixtures.map((f) => (f.gamefile_id === gamefileId
        ? { ...f, our_side: side } : f)) })
  // Sportmonks path disabled at cutover (Ben, 2026-09-10): fixture sync is
  // off, the stored data has a home/away swap on away games, and we don't
  // want stale Sportmonks rows overriding the game-code-derived truth.
  // Re-enable when Sportmonks is deliberately turned back on.
  const endpoint = '/api/v1/fixtures/games'

  useEffect(() => {
    api<Record<string, unknown>>(endpoint)
      .then((body) => {
        const list = Object.values(body).find(Array.isArray) as Fixture[] | undefined
        setState({ status: 'ready', fixtures: list ?? [] })
      })
      .catch((e: unknown) => setState({
        status: 'error',
        message: e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
      }))
    // The club, too: a switch made on another page reloads the session in
    // place, and this list must follow it.
  }, [endpoint, reload, session.org?.id])

  // The title renders in every state, so the page does not jump when the
  // data lands — the list below it is the only thing that changes.
  const heading = embedded ? null : <h1 className="page-title">{title}</h1>
  if (state.status === 'loading') {
    return (<>{heading}<p className="muted">Loading…</p></>)
  }
  if (state.status === 'error') {
    return (
      <>
        {heading}
        <div className="panel error">Could not load matches — {state.message}</div>
      </>
    )
  }

  const { fixtures } = state
  const withFilm = fixtures.filter((f) => f.gamefile_id).length
  // Newest first by default; undated matches always sink to the bottom.
  const sorted = [...fixtures].sort((x, y) => {
    if (!x.date || !y.date) return (x.date ? 0 : 1) - (y.date ? 0 : 1)
    return sortDir === 'desc' ? y.date.localeCompare(x.date) : x.date.localeCompare(y.date)
  })
  // Ours: the club plays (our_side). Opponent: two other teams — scouting.
  const ours = sorted.filter((f) => f.our_side != null)
  const opponents = sorted.filter((f) => f.our_side == null)

  const cols = canEditScore ? 5 : 4
  const table = (rows: Fixture[]) => (
    <table className="gm-table">
      <thead>
        <tr>
          <th>Home</th>
          <th>Away</th>
          <th>
            <button type="button" className="gm-sort"
                    aria-label={`Date, ${sortDir === 'desc' ? 'newest' : 'oldest'} first`}
                    onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}>
              Date {sortDir === 'desc' ? '↓' : '↑'}
            </button>
          </th>
          <th className="gm-c">Score</th>
          {canEditScore && <th>Club side · code</th>}
        </tr>
      </thead>
      {/* One body per year, newest year first (Ben, 2026-10-24); the date
          sort orders the matches inside each year. Undated matches last. */}
      {byYear(rows).map(({ year, rows: yearRows }) => (
      <tbody key={year} className="gm-year-group">
        <tr className="gm-year">
          <th colSpan={cols} scope="colgroup">
            {year} <span className="muted">{yearRows.length}</span>
          </th>
        </tr>
        {yearRows.map((f, i) => {
          const openable = !!f.gamefile_id && !!f.game_code
          // The game code contains '@', which is legal in a path segment.
          // encodeURIComponent would turn it into %40, which this app's
          // routes do not expect — so encode everything EXCEPT the '@'.
          const href = f.game_code
            ? `${rowTarget}/${pathSeg(f.game_code)}`
            : undefined
          const cells = (
            <>
              <td><Team name={f.home_team} logo={f.home_team_logo} /></td>
              <td><Team name={f.away_team} logo={f.away_team_logo} /></td>
              <td className="gm-date">
                <MatchDate gamefileId={f.gamefile_id} matchDate={f.match_date ?? null}
                           label={matchDate(f.date)} canEdit={canEditScore}
                           onSaved={(d) => setDate(f.gamefile_id, d)} />
              </td>
              <td className="gm-c">
                <MatchScore gameId={f.game_id} canEdit={canEditScore}
                            score={{ home: f.home_score, away: f.away_score }}
                            labeled={f.goal_score}
                            onSaved={(sc) => setScore(f.gamefile_id, sc)} />
                {showPpda && f.stack_served && f.game_code && (
                  <ListPpda gameCode={f.game_code} />
                )}
              </td>
              {canEditScore && (
                <td>
                  <ClubSide gameId={f.game_id} side={f.our_side}
                            onSaved={(sd) => setSide(f.gamefile_id, sd)} />
                  {f.game_code && (
                    <RenameMatch gameId={f.game_id} gameCode={f.game_code}
                                 onRenamed={() => setReload((n) => n + 1)} />
                  )}
                </td>
              )}
            </>
          )
          return openable ? (
            <tr key={f.gamefile_id ?? i} className="gm-row is-openable"
                onClick={() => navigate(href!)}
                tabIndex={0} role="link"
                onKeyDown={(e) => {
                  // Keyboard parity: a row that behaves as a link must be
                  // reachable without a mouse.
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    navigate(href!)
                  }
                }}>
              {cells}
            </tr>
          ) : (
            <tr key={f.gamefile_id ?? `x${i}`} className="gm-row" title="No film for this match">
              {cells}
            </tr>
          )
        })}
      </tbody>
      ))}
    </table>
  )

  return (
    <>
      {heading}
      <p className="page-sub">
        {session.org?.name ?? 'No club selected'} ·{' '}
        {fixtures.length} match{fixtures.length === 1 ? '' : 'es'} ·{' '}
        {withFilm} with film. {rowOpens}
      </p>

      {!embedded && <WorthALook limit={8} />}
      {!embedded && <WorthALook limit={8} side="opponents" />}

      {fixtures.length === 0 ? (
        // The empty state says what to do next, not just what is missing.
        <div className="panel muted">
          No matches yet. <Link to="/upload-match">Upload a match</Link> to get started.
        </div>
      ) : (
        <>
          <section className="gm-section">
            <h2 className="gm-section__title">Our Matches <span className="muted">{ours.length}</span></h2>
            {ours.length
              ? table(ours)
              : <p className="muted">No matches with {session.org?.name ?? 'this club'} playing.</p>}
          </section>
          {opponents.length > 0 && (
            <section className="gm-section">
              <h2 className="gm-section__title">
                Opponent Matches <span className="muted">{opponents.length}</span>
              </h2>
              <p className="muted gm-section__sub">Other teams' matches, for scouting.</p>
              {table(opponents)}
            </section>
          )}
        </>
      )}
    </>
  )
}
